// MF-568 — Card 'pat-growth-quadrants': growth quadrants for a class. x = standardised individual change (SD units),
// y = latest scale score, dashed lines at the growth threshold (meta.card.options.growthThreshold) and at the school's
// score benchmark (only when every student shares one). Without a benchmark only the growth line is drawn and
// students are NOT split into groups. Click a quadrant (chart or legend) to see its roster.
import { DetailCard, DetailTable } from './PatDetail'
import { useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Box, Typography } from '@mui/material'
import { INK, FONT, SUMMARY_PALETTE, TONE_PILL, baseTextStyle, tooltipBase } from '../boardTheme'
import { QUADRANTS, QUADRANT_KEYS, esc, fmtD, median, quadrantOf, sharedBenchmark } from './patSkillsShared'

const NEUTRAL = '#98a3b3'
const fmt2 = (n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(2) : '–')
const fmtDate = (iso) => {
  if (!iso) return '–'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}
const tint = (hex, a) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16))
  return `rgba(${r},${g},${b},${a})`
}

const PatGrowthQuadrantsCard = ({ data }) => {
  const rows = useMemo(
    () =>
      (data?.rows || []).filter((r) => Number.isFinite(Number(r.growth)) && Number.isFinite(Number(r.score))),
    [data?.rows]
  )
  const threshold = Number(data?.meta?.card?.options?.growthThreshold)
  const hasThreshold = Number.isFinite(threshold)
  const benchmark = useMemo(() => sharedBenchmark(rows), [rows])
  const anyBenchmark = rows.some((r) => r.benchmark !== null && r.benchmark !== undefined)
  const split = benchmark !== null && hasThreshold
  const [sel, setSel] = useState(null)

  const points = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        q: split
          ? quadrantOf(r.growth, r.score, threshold, benchmark)
          : anyBenchmark
            ? r.quadrant || null
            : null
      })),
    [rows, split, anyBenchmark, threshold, benchmark]
  )
  const byQ = useMemo(() => {
    const m = Object.fromEntries(QUADRANT_KEYS.map((k) => [k, []]))
    points.forEach((p) => {
      if (p.q && m[p.q]) m[p.q].push(p)
    })
    return m
  }, [points])

  const xs = points.map((p) => Number(p.growth))
  const ys = points.map((p) => Number(p.score))
  const xLo = Math.min(...xs, hasThreshold ? threshold : 0) - 0.25
  const xHi = Math.max(...xs, hasThreshold ? threshold : 0) + 0.25
  const yLo = Math.floor(Math.min(...ys, split ? benchmark : Infinity) - 3)
  const yHi = Math.ceil(Math.max(...ys, split ? benchmark : -Infinity) + 3)
  const colorOf = (p) => (p.q ? QUADRANTS[p.q].color : NEUTRAL)
  const showAllNames = points.length <= 12

  const option = useMemo(() => {
    if (!points.length) return {}
    const markLine = []
    if (hasThreshold)
      markLine.push({
        xAxis: threshold,
        label: { formatter: `Growth threshold: ${threshold}`, position: 'insideEndTop' }
      })
    if (split)
      markLine.push({
        yAxis: benchmark,
        label: { formatter: `Score benchmark: ${fmtD(benchmark)}`, position: 'insideEndTop' }
      })
    const areaDef = split
      ? [
          [
            'optimal',
            [
              { xAxis: threshold, yAxis: benchmark },
              { xAxis: xHi, yAxis: yHi }
            ]
          ],
          [
            'growth',
            [
              { xAxis: threshold, yAxis: yLo },
              { xAxis: xHi, yAxis: benchmark }
            ]
          ],
          [
            'cruising',
            [
              { xAxis: xLo, yAxis: benchmark },
              { xAxis: threshold, yAxis: yHi }
            ]
          ],
          [
            'support',
            [
              { xAxis: xLo, yAxis: yLo },
              { xAxis: threshold, yAxis: benchmark }
            ]
          ]
        ]
      : []
    return {
      textStyle: baseTextStyle,
      grid: { left: 52, right: 24, top: 16, bottom: 44 },
      tooltip: {
        ...tooltipBase,
        trigger: 'item',
        formatter: ({ data: d }) => {
          const p = d.row
          return [
            `<b>${esc(p.name)}</b>${p.year_level ? ` · ${esc(p.year_level)}` : ''}`,
            `${esc(fmtDate(p.prev_test_date))}: ${fmtD(p.prev_score)} → ${esc(fmtDate(p.test_date))}: ${fmtD(p.score)}`,
            `Raw gain: ${p.gain > 0 ? '+' : ''}${fmtD(p.gain)} · Standardised change: ${fmt2(p.growth)} SD`,
            p.benchmark !== null && p.benchmark !== undefined
              ? `Benchmark: ${fmtD(p.benchmark)}`
              : 'No benchmark set',
            p.q ? `Group: ${QUADRANTS[p.q].label}` : ''
          ]
            .filter(Boolean)
            .join('<br/>')
        }
      },
      xAxis: {
        type: 'value',
        name: 'Standardised change (SD units)',
        nameLocation: 'middle',
        nameGap: 28,
        min: xLo,
        max: xHi,
        axisLabel: { ...baseTextStyle, formatter: (v) => fmt2(v) },
        splitLine: { lineStyle: { color: INK.grid } },
        nameTextStyle: baseTextStyle
      },
      yAxis: {
        type: 'value',
        name: 'Latest scale score',
        nameLocation: 'middle',
        nameGap: 38,
        min: yLo,
        max: yHi,
        axisLabel: baseTextStyle,
        splitLine: { lineStyle: { color: INK.grid } },
        nameTextStyle: baseTextStyle
      },
      series: [
        {
          type: 'scatter',
          symbolSize: 11,
          data: points.map((p) => ({
            value: [Number(p.growth), Number(p.score)],
            row: p,
            itemStyle: {
              color: colorOf(p),
              opacity: sel && p.q !== sel ? 0.25 : 0.95,
              borderColor: '#fff',
              borderWidth: 1.5
            },
            label: {
              show: showAllNames || sel === p.q,
              formatter: p.name,
              position: 'right',
              fontFamily: FONT,
              fontSize: 10.5,
              color: INK.secondary
            }
          })),
          markLine: markLine.length
            ? {
                silent: true,
                symbol: 'none',
                lineStyle: { type: 'dashed', color: INK.secondary, width: 1.2 },
                label: { fontFamily: FONT, fontSize: 11, color: INK.secondary },
                data: markLine
              }
            : undefined,
          markArea: areaDef.length
            ? {
                silent: false,
                label: { show: false },
                data: areaDef.map(([k, pair]) => [
                  {
                    name: k,
                    ...pair[0],
                    itemStyle: { color: tint(QUADRANTS[k].color, sel === k ? 0.22 : 0.09) }
                  },
                  pair[1]
                ])
              }
            : undefined
        }
      ]
    }
  }, [points, hasThreshold, threshold, split, benchmark, sel, xLo, xHi, yLo, yHi]) // eslint-disable-line react-hooks/exhaustive-deps

  const onEvents = {
    click: (e) => {
      const key = e.componentType === 'markArea' ? e.name : e.data?.row?.q
      if (key && QUADRANTS[key]) setSel((s) => (s === key ? null : key))
    }
  }

  const months = median(rows.map((r) => Number(r.months_between)))
  const farFrom12 = months !== null && Math.abs(months - 12) > 3
  const first = rows[0] || {}
  const roster = sel ? byQ[sel] : []

  const selectable = (key) => ({
    role: 'button',
    tabIndex: 0,
    'aria-pressed': sel === key,
    onClick: () => setSel((s) => (s === key ? null : key)),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setSel((s) => (s === key ? null : key))
      }
    }
  })

  return (
    <Box
      sx={{
        height: '100%',
        minWidth: 0,
        overflow: 'auto',
        fontFamily: FONT,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5
      }}
    >
      <Box sx={{ flex: '1 0 280px', minHeight: 280, minWidth: 0 }}>
        <ReactECharts
          option={option}
          onEvents={onEvents}
          notMerge
          style={{ height: '100%', minHeight: 280 }}
        />
      </Box>

      {!split ? (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: '8px',
            bgcolor: '#eef2f7',
            color: SUMMARY_PALETTE.ink,
            fontSize: 12.5
          }}
        >
          {anyBenchmark
            ? 'These students have different score benchmarks (different year levels or tests), so the benchmark line is not drawn and they are not split into the four groups here.'
            : "The score benchmark comes from the school's norms (Board Builder → PAT settings); until then students are not split into the four groups."}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 1 }}>
          {QUADRANT_KEYS.map((k) => {
            const q = QUADRANTS[k]
            const list = byQ[k]
            return (
              <Box
                key={k}
                {...selectable(k)}
                sx={{
                  cursor: 'pointer',
                  p: 1.25,
                  borderRadius: '8px',
                  border: `1.5px solid ${sel === k ? q.color : SUMMARY_PALETTE.cardBorder}`,
                  bgcolor: sel === k ? tint(q.color, 0.1) : '#fff',
                  outlineColor: q.color
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: q.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: SUMMARY_PALETTE.ink }}>
                    {q.label} ({list.length})
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 11, color: SUMMARY_PALETTE.muted }}>{q.hint}</Typography>
                <Typography sx={{ fontSize: 11.5, color: SUMMARY_PALETTE.ink, mt: 0.5 }}>
                  {list.length ? list.map((p) => p.name).join(', ') : 'No students'}
                </Typography>
              </Box>
            )
          })}
        </Box>
      )}

      {sel ? (
        <DetailCard title={QUADRANTS[sel].label} count={roster.length} onClear={() => setSel(null)}>
          <DetailTable
            rowKey={(p) => p.student_id}
            rows={roster}
            columns={[
              { key: 'name', label: 'Student', wrap: true },
              {
                key: 'prev',
                label: 'Previous',
                render: (p) => (
                  <>
                    {fmtD(p.prev_score)}{' '}
                    <span style={{ color: SUMMARY_PALETTE.faint }}>({fmtDate(p.prev_test_date)})</span>
                  </>
                )
              },
              {
                key: 'latest',
                label: 'Latest',
                render: (p) => (
                  <>
                    {fmtD(p.score)}{' '}
                    <span style={{ color: SUMMARY_PALETTE.faint }}>({fmtDate(p.test_date)})</span>
                  </>
                )
              },
              {
                key: 'gain',
                label: 'Raw gain',
                align: 'right',
                bold: true,
                render: (p) => `${p.gain > 0 ? '+' : ''}${fmtD(p.gain)}`
              },
              {
                key: 'growth',
                label: 'Standardised change',
                align: 'right',
                render: (p) => `${fmt2(p.growth)} SD`
              }
            ]}
          />
        </DetailCard>
      ) : null}

      {farFrom12 ? (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: '8px',
            bgcolor: TONE_PILL.negative.bg,
            color: TONE_PILL.negative.fg,
            fontSize: 12
          }}
        >
          The interval between tests is about {fmtD(months)} months; comparable 12-month intervals
          recommended.
        </Box>
      ) : null}

      <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, lineHeight: 1.6 }}>
        {rows.length} matched students · pooled SD {fmt2(first.pooled_sd)} · group d {fmt2(first.group_d)}{' '}
        (descriptive pre/post standardised mean difference, not an intervention effect)
        {months !== null ? ` · interval ${fmtD(months)} months` : ''}. Circles show observed change, not
        causal intervention effects. Review evidence before assigning support. Labels describe this assessment
        interval.
      </Typography>
    </Box>
  )
}

export default PatGrowthQuadrantsCard
